import { useCallback } from 'react';
import { redirect, useNavigate } from '@tanstack/react-router';
import { DOMAIN_NAME_PREFIX, FIRST_CRITERION_ITEM, parseItem, type HrTab } from './model';

/**
 * Where a Health & Readiness link lands (T-0242 / E22 design §1): one tab
 * per URL, the row on Domains and Go-live criteria in the path.
 *
 *   /engagements/$engagementId/health-readiness                 Overview
 *   …/domains, …/domains/$domainId                              Domains
 *   …/criteria, …/criteria/$criterionId                         Go-live criteria
 *   …/findings?domain=&severity=&status=                        Findings
 *   …/interviews, …/report, …/snapshots
 *
 * E20's `?item=` and every old URL map onto these through `targetOfItem`,
 * which needs no data: only an old link's `domain-name:<name>` hint (the
 * Budget and Schedule tabs) stays on the Overview's `?item=`, where the
 * composite read resolves it to the domain's id.
 */

export interface FindingsFilters {
  severity?: string;
  status?: string;
  domain?: string;
}

export type HrTarget =
  | { tab: 'overview'; hint?: string }
  | { tab: 'domains'; id?: string }
  | { tab: 'criteria'; id?: string }
  | { tab: 'findings'; filters?: FindingsFilters }
  | { tab: 'interviews' }
  | { tab: 'report' }
  | { tab: 'snapshots' };

/** An E20 row key (`domain:<id>`, `findings`, a hint, …) as a target. */
export function targetOfItem(
  raw: string | null | undefined,
  filters: FindingsFilters = {},
): HrTarget {
  if (!raw) return { tab: 'overview' };
  if (raw === FIRST_CRITERION_ITEM) return { tab: 'criteria' };
  if (raw.startsWith(DOMAIN_NAME_PREFIX)) return { tab: 'overview', hint: raw };
  const item = parseItem(raw);
  if (!item) return { tab: 'overview' };
  switch (item.kind) {
    case 'domain':
      return { tab: 'domains', id: item.id };
    case 'criterion':
      return { tab: 'criteria', id: item.id };
    case 'findings':
      return { tab: 'findings', filters };
    default:
      return { tab: item.kind };
  }
}

const BASE = '/engagements/$engagementId/health-readiness';

export interface HrLocation {
  to: string;
  params: Record<string, string>;
  search: Record<string, string>;
}

function only(filters: FindingsFilters | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of ['domain', 'severity', 'status'] as const) {
    const v = filters?.[key];
    if (v) out[key] = v;
  }
  return out;
}

export function hrLocation(engagementId: string, target: HrTarget): HrLocation {
  const params = { engagementId };
  switch (target.tab) {
    case 'overview':
      return { to: BASE, params, search: target.hint ? { item: target.hint } : {} };
    case 'domains':
      return target.id
        ? {
            to: `${BASE}/domains/$domainId`,
            params: { ...params, domainId: target.id },
            search: {},
          }
        : { to: `${BASE}/domains`, params, search: {} };
    case 'criteria':
      return target.id
        ? {
            to: `${BASE}/criteria/$criterionId`,
            params: { ...params, criterionId: target.id },
            search: {},
          }
        : { to: `${BASE}/criteria`, params, search: {} };
    case 'findings':
      return { to: `${BASE}/findings`, params, search: only(target.filters) };
    default:
      return { to: `${BASE}/${target.tab satisfies HrTab}`, params, search: {} };
  }
}

/** The `beforeLoad` redirect an old URL throws: `replace`, so Back skips it. */
export function hrRedirect(engagementId: string, target: HrTarget) {
  // The location is built from a union of the tab paths; the router's
  // per-path typing cannot follow a union, so it is checked here instead.
  const options: unknown = { ...hrLocation(engagementId, target), replace: true };
  return redirect(options as Parameters<typeof redirect>[0]);
}

/** Navigate to a target inside Health & Readiness. */
export function useHrNavigate(engagementId: string) {
  const navigate = useNavigate();
  return useCallback(
    (target: HrTarget, opts: { replace?: boolean } = {}) => {
      const options: unknown = {
        ...hrLocation(engagementId, target),
        replace: opts.replace ?? false,
      };
      void navigate(options as Parameters<typeof navigate>[0]);
    },
    [navigate, engagementId],
  );
}
